export type HandledCallSource = 'local-accept' | 'local-reject' | 'remote-terminal'

export interface HandledCallRecord {
  source: HandledCallSource
  handledAt: number
}

export type HandledCallState = Map<string, HandledCallRecord>

function getContactKey(contactId: string): string {
  return `contact:${contactId}`
}

function getHeaderKey(callHeaderId: string | number): string {
  return `header:${normalizeCallHeaderId(callHeaderId)}`
}

export function createHandledCallState(): HandledCallState {
  return new Map()
}

export function normalizeCallHeaderId(callHeaderId: string | number): string {
  const value = String(callHeaderId).trim()
  const numericPart = value.match(/^(?:OPI-)?0*(\d+)$/i)
  return numericPart ? numericPart[1] : value.toUpperCase()
}

/**
 * Numeric call-header code for set-ended-by-interpreter / End Call.
 * Same parse as manual End Call: first digit run in the string.
 */
export function parseCallHeaderCode(
  callHeaderId: string | number | null | undefined,
): number | null {
  if (callHeaderId == null) return null
  const numericMatch = String(callHeaderId).match(/\d+/)
  if (!numericMatch) return null
  const code = parseInt(numericMatch[0], 10)
  return Number.isNaN(code) ? null : code
}

export function markCallHandled(
  state: HandledCallState,
  contactId: string,
  callHeaderId: string | number,
  source: HandledCallSource,
  handledAt: number = Date.now()
): void {
  const record = {source, handledAt}
  state.set(getContactKey(contactId), record)
  state.set(getHeaderKey(callHeaderId), record)
}

export function findHandledCall(
  state: HandledCallState,
  contactId: string,
  callHeaderId: string | number
): HandledCallRecord | null {
  return state.get(getContactKey(contactId)) ?? state.get(getHeaderKey(callHeaderId)) ?? null
}

export function removeHandledCall(
  state: HandledCallState,
  contactId: string,
  callHeaderId: string | number
): void {
  state.delete(getContactKey(contactId))
  state.delete(getHeaderKey(callHeaderId))
}

/** SignalR call:accepted / call:disconnected / call:routed_externally are terminal for this client. */
export function markRemoteCallDismissed(
  state: HandledCallState,
  contactId: string,
  callHeaderId: string | number,
  handledAt: number = Date.now()
): void {
  markCallHandled(state, contactId, callHeaderId, 'remote-terminal', handledAt)
}
